import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Check, X, Plus, Trash2, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pendingReviews, setPendingReviews] = useState([]);
  const [news, setNews] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // News form
  const [newsTitle, setNewsTitle] = useState('');
  const [newsContent, setNewsContent] = useState('');
  const [newsImage, setNewsImage] = useState('');
  const [newsDialogOpen, setNewsDialogOpen] = useState(false);
  
  // Company form
  const [companyName, setCompanyName] = useState('');
  const [companyDesc, setCompanyDesc] = useState('');
  const [companyLogo, setCompanyLogo] = useState('');
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [reviewsRes, newsRes, companiesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/reviews/pending`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/news`),
        axios.get(`${BACKEND_URL}/api/companies`)
      ]);
      setPendingReviews(reviewsRes.data);
      setNews(newsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error(t('toast.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApproveReview = async (reviewId) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/reviews/${reviewId}/approve`,
        {},
        { withCredentials: true }
      );
      toast.success(t('toast.reviewApproved'));
      fetchData();
    } catch (error) {
      toast.error(t('toast.failedToApprove'));
    }
  };

  const handleRejectReview = async (reviewId) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/reviews/${reviewId}/reject`,
        {},
        { withCredentials: true }
      );
      toast.success(t('toast.reviewRejected'));
      fetchData();
    } catch (error) {
      toast.error(t('toast.failedToReject'));
    }
  };

  const handleCreateNews = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${BACKEND_URL}/api/news`,
        {
          title: newsTitle,
          content: newsContent,
          image_url: newsImage || null
        },
        { withCredentials: true }
      );
      toast.success(t('toast.newsCreated'));
      setNewsTitle('');
      setNewsContent('');
      setNewsImage('');
      setNewsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(t('toast.failedToCreateNews'));
    }
  };

  const handleDeleteNews = async (newsId) => {
    try {
      await axios.delete(
        `${BACKEND_URL}/api/news/${newsId}`,
        { withCredentials: true }
      );
      toast.success(t('toast.newsDeleted'));
      fetchData();
    } catch (error) {
      toast.error(t('toast.failedToDeleteNews'));
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${BACKEND_URL}/api/companies`,
        {
          name: companyName,
          description: companyDesc,
          logo_url: companyLogo || null
        },
        { withCredentials: true }
      );
      toast.success(t('toast.companyCreated'));
      setCompanyName('');
      setCompanyDesc('');
      setCompanyLogo('');
      setCompanyDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(t('toast.failedToCreateCompany'));
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'fill-yellow-500 text-yellow-500' : 'text-slate-300'}`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="inline-flex items-center text-blue-900 hover:text-blue-800" data-testid="back-to-home">
            <ArrowLeft className="w-5 h-5 mr-2" />
            <span className="font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('common.backToHome')}</span>
          </Link>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {t('admin.dashboard')}
          </h1>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12 space-y-8">
        {/* Pending Reviews */}
        <section>
          <h2 className="text-3xl font-black text-slate-900 mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {t('admin.pendingReviews')} ({pendingReviews.length})
          </h2>
          
          {loading ? (
            <div className="text-slate-600">{t('common.loading')}</div>
          ) : pendingReviews.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
              <p className="text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {t('admin.noPending')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingReviews.map((review) => (
                <div
                  key={review.id}
                  data-testid={`pending-review-${review.id}`}
                  className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        {review.company_name}
                      </h3>
                      <div className="flex items-center gap-3 mt-1">
                        {renderStars(review.rating)}
                        <span className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          by {review.reviewer_name}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleApproveReview(review.id)}
                        data-testid={`approve-btn-${review.id}`}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        size="sm"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        {t('admin.approve')}
                      </Button>
                      <Button
                        onClick={() => handleRejectReview(review.id)}
                        data-testid={`reject-btn-${review.id}`}
                        variant="outline"
                        className="border-red-600 text-red-600 hover:bg-red-50"
                        size="sm"
                      >
                        <X className="w-4 h-4 mr-1" />
                        {t('admin.reject')}
                      </Button>
                    </div>
                  </div>
                  <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {review.comment}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Companies Management */}
        <section>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-3xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('admin.companies')} ({companies.length})
            </h2>
            <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-company-btn" className="bg-blue-900 text-white hover:bg-blue-800">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('admin.addCompany')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>{t('admin.addNewCompany')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateCompany} className="space-y-4">
                  <div>
                    <Label htmlFor="company-name">{t('admin.companyName')}</Label>
                    <Input
                      id="company-name"
                      data-testid="company-name-input"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company-desc">{t('admin.description')}</Label>
                    <Textarea
                      id="company-desc"
                      data-testid="company-desc-input"
                      value={companyDesc}
                      onChange={(e) => setCompanyDesc(e.target.value)}
                      required
                      className="mt-1"
                      rows={4}
                    />
                  </div>
                  <div>
                    <Label htmlFor="company-logo">{t('admin.logoUrl')}</Label>
                    <Input
                      id="company-logo"
                      data-testid="company-logo-input"
                      value={companyLogo}
                      onChange={(e) => setCompanyLogo(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <Button type="submit" data-testid="submit-company-btn" className="w-full bg-blue-900 text-white hover:bg-blue-800">
                    {t('admin.createCompany')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {companies.map((company) => (
              <div
                key={company.id}
                data-testid={`company-${company.id}`}
                className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm"
              >
                <h3 className="font-bold text-slate-900 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {company.name}
                </h3>
                <div className="flex items-center gap-2 mb-2">
                  {renderStars(Math.round(company.average_rating))}
                  <span className="text-sm text-slate-600">
                    {company.average_rating.toFixed(1)} ({company.review_count})
                  </span>
                </div>
                <p className="text-sm text-slate-600 line-clamp-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {company.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* News Management */}
        <section>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-3xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('admin.news')} ({news.length})
            </h2>
            <Dialog open={newsDialogOpen} onOpenChange={setNewsDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-news-btn" className="bg-blue-900 text-white hover:bg-blue-800">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('admin.addNews')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>{t('admin.addNewsItem')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateNews} className="space-y-4">
                  <div>
                    <Label htmlFor="news-title">{t('admin.title')}</Label>
                    <Input
                      id="news-title"
                      data-testid="news-title-input"
                      value={newsTitle}
                      onChange={(e) => setNewsTitle(e.target.value)}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="news-content">{t('admin.content')}</Label>
                    <Textarea
                      id="news-content"
                      data-testid="news-content-input"
                      value={newsContent}
                      onChange={(e) => setNewsContent(e.target.value)}
                      required
                      className="mt-1"
                      rows={4}
                    />
                  </div>
                  <div>
                    <Label htmlFor="news-image">{t('admin.imageUrl')}</Label>
                    <Input
                      id="news-image"
                      data-testid="news-image-input"
                      value={newsImage}
                      onChange={(e) => setNewsImage(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <Button type="submit" data-testid="submit-news-btn" className="w-full bg-blue-900 text-white hover:bg-blue-800">
                    {t('admin.createNews')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          
          <div className="space-y-4">
            {news.map((item) => (
              <div
                key={item.id}
                data-testid={`news-${item.id}`}
                className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {item.title}
                    </h3>
                    <p className="text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      {item.content}
                    </p>
                  </div>
                  <Button
                    onClick={() => handleDeleteNews(item.id)}
                    data-testid={`delete-news-${item.id}`}
                    variant="outline"
                    className="border-red-600 text-red-600 hover:bg-red-50"
                    size="sm"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminDashboard;
